import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSettings } from 'browser/components/RepAI/Inbound/api/getSettings';
import { registrationApi } from 'browser/components/RepAI/Inbound/api/registration';
import { updateSettings } from 'browser/components/RepAI/Inbound/api/updateSettings';
import type {
  A2PRegistrationStatus,
  ConfigureA2PPhoneNumberPayload,
  InboundConfig,
  InboundState,
  PhoneNumber,
} from 'browser/components/RepAI/Inbound/types';
import { TrusthubRegistrationBody } from 'types/outbound';

const initialState: InboundState = {
  data: {
    id: null,
    behavior: '',
    introMessage: '',
    voiceId: '',
    phoneNumbers: [],
    emailAddress: '',
  },
  loaded: false,
  updateStatus: 'idle',
  a2pRegistrationCompleted: false,
};

export const fetchInboundSettings = createAsyncThunk(
  'aiInbound/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const data: InboundConfig = await getSettings();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const updateInboundSettings = createAsyncThunk(
  'aiInboundSettings/update',
  async (settings: InboundConfig) => {
    await updateSettings(settings);
    return settings;
  },
);

export const registerCompanyProfile = createAsyncThunk(
  'a2p/registerCompanyProfile',
  async (payload: TrusthubRegistrationBody, { rejectWithValue }) => {
    try {
      return await registrationApi.registerBusinessProfile(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const configureBusinessForA2P = createAsyncThunk(
  'a2p/configureBusinessForA2P',
  async () => {
    return await registrationApi.configureBusinessForA2P();
  },
);

export const registerA2pCampaign = createAsyncThunk(
  'a2p/registerA2pCampaign',
  async () => {
    return await registrationApi.registerA2pCampaign();
  },
);

export const registrationProgress = createAsyncThunk(
  'a2p/registrationProgress',
  async () => {
    return await registrationApi.getProgress();
  },
);

export const addPhoneNumberToCampaign = createAsyncThunk(
  'a2p/configurePhoneNumber',
  async (payload: ConfigureA2PPhoneNumberPayload) => {
    return await registrationApi.configurePhoneNumberForCampaign(payload);
  },
);

const slice = createSlice({
  name: 'aiInboundSettings',
  initialState,
  reducers: {
    setInboundSettings: (state, action) => {
      state.data = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchInboundSettings.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loaded = true;
    });
    builder.addCase(updateInboundSettings.pending, (state, _) => {
      state.updateStatus = 'pending';
      state.errorMessage = null;
    });
    builder.addCase(updateInboundSettings.fulfilled, (state, _) => {
      state.updateStatus = 'succeeded';
    });
    builder.addCase(updateInboundSettings.rejected, (state, action) => {
      state.updateStatus = 'failed';
      state.errorMessage = action.error.message;
    });
    builder.addCase(registrationProgress.fulfilled, (state, action) => {
      state.a2pRegistrationStatus = action.payload;

      const allCompleted =
        action.payload.length > 0 &&
        action.payload.every((s: A2PRegistrationStatus) => s.status === 'COMPLETED');
      state.a2pRegistrationCompleted = allCompleted;
    });
    builder.addCase(addPhoneNumberToCampaign.fulfilled, (state, action) => {
      const updated = action.payload as PhoneNumber;

      const existing = state.data.phoneNumbers.find(pn => pn.sid === updated.sid);
      if (existing) Object.assign(existing, updated);
    });
  },
});

export const { setInboundSettings } = slice.actions;
export default slice.reducer;
